import { create } from 'zustand';

import { db } from '@/services/storage/db';
import { ShoppingListService } from '@/services/shopping-list-service';
import type { ShoppingListItem, CreateShoppingListItemInput, UpdateShoppingListItemInput } from '@/types/shopping-list';

const shoppingListService = new ShoppingListService(db);

interface ShoppingListStore {
  items: ShoppingListItem[];
  isLoading: boolean;
  error: string | null;

  loadItems: (propertyId?: string) => Promise<void>;
  addItem: (data: CreateShoppingListItemInput) => Promise<void>;
  updateItem: (id: string, data: UpdateShoppingListItemInput) => Promise<void>;
  togglePurchased: (id: string) => Promise<void>;
  deleteItem: (id: string) => Promise<void>;
}

export const useShoppingListStore = create<ShoppingListStore>((set, get) => ({
  items: [],
  isLoading: false,
  error: null,

  loadItems: async (propertyId) => {
    set({ isLoading: true, error: null });
    try {
      const items = await shoppingListService.getShoppingList(propertyId);
      set({ items, isLoading: false });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load shopping list';
      console.error('ShoppingListStore.loadItems:', err);
      set({ error: message, isLoading: false });
    }
  },

  addItem: async (data) => {
    try {
      const item = await shoppingListService.addItem(data);
      set((state) => ({ items: [...state.items, item] }));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to add item';
      console.error('ShoppingListStore.addItem:', err);
      set({ error: message });
    }
  },

  updateItem: async (id, data) => {
    try {
      const updated = await shoppingListService.updateItem(id, data);
      set((state) => ({
        items: state.items.map((i) => (i.id === id ? updated : i)),
      }));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update item';
      console.error('ShoppingListStore.updateItem:', err);
      set({ error: message });
    }
  },

  togglePurchased: async (id) => {
    try {
      // Optimistic update
      const current = get().items.find((i) => i.id === id);
      if (current) {
        set((state) => ({
          items: state.items.map((i) =>
            i.id === id ? { ...i, purchased: !i.purchased } : i,
          ),
        }));
      }
      const updated = await shoppingListService.togglePurchased(id);
      set((state) => ({
        items: state.items.map((i) => (i.id === id ? updated : i)),
      }));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to toggle purchased';
      console.error('ShoppingListStore.togglePurchased:', err);
      // Revert optimistic update
      const items = await shoppingListService.getShoppingList();
      set({ items, error: message });
    }
  },

  deleteItem: async (id) => {
    try {
      await shoppingListService.deleteItem(id);
      set((state) => ({ items: state.items.filter((i) => i.id !== id) }));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete item';
      console.error('ShoppingListStore.deleteItem:', err);
      set({ error: message });
    }
  },
}));

/** Selector: count of unpurchased items */
export function selectUnpurchasedCount(items: ShoppingListItem[]): number {
  return items.filter((i) => !i.purchased).length;
}

/** Selector: items grouped by category (purchased items excluded from main groups) */
export function selectItemsByCategory(
  items: ShoppingListItem[],
): Record<string, ShoppingListItem[]> {
  const groups: Record<string, ShoppingListItem[]> = {};
  for (const item of items) {
    if (item.purchased) continue;
    const cat = item.category ?? 'other';
    if (!groups[cat]) groups[cat] = [];
    groups[cat].push(item);
  }
  return groups;
}
